import { ChangeDetectionStrategy, Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ButtonComponent, ConfirmModalComponent, ConfirmModalData, SnackbarComponent, ToastType } from '@program-suite/ui';
import { GridEmitter, GridOptions, UiGridComponent } from '@program-suite/ui-grid';
import { DocumentGridRowViewModel, PolicyChangeGridViewModel, SubmissionStatusEnum, SubmissionSummaryViewModel, ViewSubmissionStore } from '@program-suite/data-common';
import { AuthStore } from '@program-suite/data-auth';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService, ModalType } from '@program-suite/ui';
import { PendingRequestModalComponent } from '@program-suite/feature-pending-requests';
import { SubmissionReviewModalComponent } from '../submission-review-modal/submission-review-modal.component';
import { PolicyEndorsementFormComponent } from '@program-suite/feature-policy-endorsements';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'lib-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss', '../view-submission.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DatePipe, ButtonComponent, UiGridComponent],
  providers: [DatePipe],
})
export class SummaryComponent implements OnInit {
  pageSizeMax = 2147483647;
  changeTabEvent = output<string>();
  viewSubmissionStore = inject(ViewSubmissionStore);
  authStore = inject(AuthStore);
  id: string | null = null;
  summary = computed<SubmissionSummaryViewModel>(() => {
    return this.viewSubmissionStore.summary?.() ?? {};
  });

  isPolicyPage: boolean = false;
  spinnerActive = computed(() => this.viewSubmissionStore.isLoading?.() ?? false);

  // LIO is third-party issued: once Bound, the underwriter uploads the signed policy PDF (stored as a
  // Policy submission document) and anyone can download it. There is no generated policy/quote package.
  isLio = computed(() => this.summary().programName === 'LIO');
  isBound = computed(() => this.summary().status === 'Bound');
  policyDocument = computed<DocumentGridRowViewModel | undefined>(() => {
    return (this.viewSubmissionStore.documents?.() ?? [])
      .filter((d) => d.documentType === 'Policy')
      .sort((a, b) => (b.createdDate ?? '').localeCompare(a.createdDate ?? ''))[0];
  });
  isEndorsementsVisible = false;
  numberOfMoreEndorsements = computed(() => {
    return (this.viewSubmissionStore.endorsements?.()?.rows?.length ?? 0) - this.totalNumberOfItems();
  });
  totalNumberOfItems = computed<number>(() => this.data()?.length ?? 0);
  // TODO: Add type for Endorsements
  data = computed<any[]>(() => this.viewSubmissionStore.endorsements?.()?.rows?.slice(0, 3) ?? []);
  filterValues = signal<any>({pageSize: this.pageSizeMax});
  gridOptions = signal<GridOptions>({
    filterData: [],
    filterValues: this.filterValues,
    openFilters: true,
    columns: [
      { header: 'Created', controlName: 'createDate', displayPipe: 'date' },
      { header: 'Type', controlName: 'changeType', displayPipe: 'space' },
      { header: 'Description', controlName: 'policyChangeType', displayPipe: 'space' },
      { header: 'Number', controlName: 'endorsementNumber' },
    ],
    data: this.data,
    frontEndPagination: false,
    fetchData: false,
    displaySearch: false,
    hidepagination: true,
    hideSort: true,
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private datePipe: DatePipe,
    private modalService: ModalService,
    private snackbar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.id = this.route.snapshot.paramMap.get('id');
    this.isPolicyPage = this.route.snapshot.url[0].path == 'policies';
    // Load documents so the LIO policy-document upload/download controls can reflect current state.
    if (this.id) {
      this.viewSubmissionStore.getSubmissionDocuments(this.id, this.isPolicyPage);
    }
  }

  downloadPolicy() {
    const doc = this.policyDocument();
    if (!doc?.id) {
      return;
    }
    this.viewSubmissionStore.setLoading(true);
    this.viewSubmissionStore.downloadDocument(doc.id, true).finally(() => this.viewSubmissionStore.setLoading(false));
  }

  async uploadPolicy(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append('files', file);
    formData.append('documentType', 'Policy');
    formData.append('guid', this.id ?? '');
    this.viewSubmissionStore.setLoading(true);
    try {
      await this.viewSubmissionStore.uploadSubmissionDocument(formData, this.isPolicyPage);
      await this.viewSubmissionStore.getSubmissionDocuments(this.id ?? '', this.isPolicyPage);
      this.snackbar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: { toastType: ToastType.Success, message: 'Policy document uploaded.' },
      });
    } catch (error: any) {
      this.snackbar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: { toastType: ToastType.Error, message: error?.message ?? 'Failed to upload the policy document.' },
      });
    } finally {
      input.value = '';
      this.viewSubmissionStore.setLoading(false);
    }
  }

  quoteAction(viewOnly?: boolean) {
    // TODO: Add logic for viewOnly
    this.viewSubmissionStore.setLoading(true);
    // LIO has its own wizard. Its classifications/payroll hang off locations[].liabilityClasses, which the
    // RCIP wizard (it reads the flat `classes` list) can't render — hence the blank fields on its 2nd step.
    this.router.navigate([this.isLio() ? '/lio-quote' : '/quote', this.id]).then(() => {
      this.viewSubmissionStore.setLoading(false);
    });
  }
  openAddEndorsementModal(policyChangeType: string) {
    const data = {
      policyGuid: this.id,
      policyNumber: this.summary()?.policyNumber,
      insuredName: this.summary()?.businessInfo?.businessName,
      policyChangeType: policyChangeType,
    };
    this.modalService.open(
      PolicyEndorsementFormComponent,
      {
        // Todo: fix modal sizes. Small is bigger than medium.
        modalType: ModalType.Small,
        data: data,
      },
      (result) => {
        if (result) {
          if(result){ 
          this.viewSubmissionStore.getSubmissionSummary(this.id!, this.isPolicyPage);
          }
        }
      },
    );
  }
  downloadQuote() {
    this.viewSubmissionStore.setLoading(true);
    this.viewSubmissionStore.downloadDocument('c-' + this.id, true).then(() => {
      this.viewSubmissionStore.setLoading(false);
    });
  }

    downloadBrokerCopy() {
    this.viewSubmissionStore.setLoading(true);
    this.viewSubmissionStore.downloadDocument('b-' + this.id, true).then(() => {
      this.viewSubmissionStore.setLoading(false);
    });
  }

  GoToTab(tab: string) {
    this.changeTabEvent.emit(tab);
  }

  bindRequest() {}

  // TODO: Consider moving actions to the parent component, view-submission.component.ts, to avoid code duplication
  reviewSubmission() {
    const data = {
      id: this.id,
      status: this.summary().status,
    };
    this.modalService.open(SubmissionReviewModalComponent, {
      modalType: ModalType.FullScreen,
      data: data,
    });
  }

  toggleEndorsements() {
    this.isEndorsementsVisible = !this.isEndorsementsVisible;
  }

  actionEvent(event: GridEmitter) {
    // No action needed for now
  }

  updateContactInfo() {}

  setStatusAsProducerReview() {
    const confirmData: ConfirmModalData = {
      confirmString: 'CONFIRM',
      title: 'Are you sure?',
      subtitle: '',
      message: `This will send your submission to the producer, ${this.summary().producerName}, for review.`,
      confirmButtonText: 'Send to Producer',
    };
    this.modalService.open(ConfirmModalComponent, {
      modalType: ModalType.Small,
      data: confirmData,
    }, (response: any) => {
      if (!response) {
        return;
      }
      var changeStatus = SubmissionStatusEnum.ProducerReviewQuote;
      if (this.summary().status === 'Producer Review Bind') {
        changeStatus = SubmissionStatusEnum.ProducerReviewBind;
      }
      this.viewSubmissionStore
        .updateSubmissionStatus(this.id ?? '', changeStatus)
        .then(() => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Success, message: 'Successfully sent to producer for review.' },
          });
          this.viewSubmissionStore.getSubmissionSummary(this.id!, this.isPolicyPage);
        }).catch((error) => {
        this.snackbar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          data: { toastType: ToastType.Error, message: error.message }
        });
      });
    });
  }
  // TODO: Consider moving actions to the parent component, view-submission.component.ts, to avoid code duplication
  adminOnlyRequestModel() {
    const pendingrequest = {
      id: this.id,
      requestType: this.summary().requestType,
    };
    this.modalService.open(
      PendingRequestModalComponent,
      {
        modalType: ModalType.FullScreen,
        data: pendingrequest,
      },
      (data?: any) => {
          this.viewSubmissionStore.getSubmissionSummary(this.id!,false);
      },
    );
  }

  //#region bannerAction Computed by Role and Status
  bannerActionTitle = computed(() => {
    var title = '';
    switch (this.summary().status) {
      case 'Producer Review':
        if(this.summary().requestType == "Rewrite"){
        title = 'Rewrite Quote Ready';
        }
        else{
        title = 'Renewal Quote Ready';
        }
        break;
      case 'Indication':
        title = this.authStore.isWholesale() ? 'Continue Submission' : 'Continue Your Submission';
        break;
      case 'Submitted':
        title = this.authStore.isAdmin() ? 'Quote Requested' : 'Application Submitted!';
        break;
      case 'Quoted':
        title = 'Submission Approved!';
        break;
      case 'Carrier Review':
        if(this.summary().requestType == "Rewrite")
        {
          title = 'New Rewrite Request'
        }
        else{
        title = 'New Renewal Request';
        }
        break;
      case 'More Info':
        title = 'Underwriter Review';
        break;
      case 'Admin Review':
          if(this.summary().requestType == "Rewrite")
        {
          title = 'New Rewrite Request'
        }
        else{
        title = 'New Renewal Request';
        }
        break;
      case 'Bind Request':
        title = this.authStore.isAdmin() ? 'Bind Requested' : 'Underwriter Review';
        break;
      case 'Submission Declined':
        title = 'Submission Declined';
        break;
      case 'Bind Request Declined':
        title = 'Bind Request Declined';
        break;
      case 'Underwriter Review Quote':
      case 'Underwriter Review Bind':
      case 'Producer Review Quote':
      case 'Producer Review Bind':
        title = 'More Information Needed';
        break;
      case 'Archived':
        break;
      case 'Bound':
        break;
      case 'Issued':
        title = 'Policy Issued';
        break;
      case 'Pending Cancellation':
        title = 'Policy Pending Cancellation';
        break;
      case 'Cancelled':
        title = 'Policy Cancelled';
        break;
      case 'Flatted':
        title = 'Policy Cancelled (Flatted)';
        break;
    }
    return title;
  });

  bannerActionDescription = computed(() => {
    var description = '';
    var lastModifiedDate = this.datePipe.transform(this.summary().lastModifiedDate, 'MM/dd/yyyy');
    switch (this.summary().status) {
      case 'Producer Review':
           if(this.summary().requestType == "Rewrite"){
              description='The quote for an upcoming policy rewrite is ready for review. Download your submission document now. Should you need any revisions, contact your underwriter.';
  
           }
           else{
            description='The quote for an upcoming policy renewal is ready for review. Download your submission document now. Should you need any revisions, contact your underwriter.';
           }
        break
        case 'Indication':
        description = this.authStore.isWholesale()
          ? 'Quote request is in progress. Complete the application and create a submission today.'
          : 'Your quote request is in progress. Complete the application and create your submission today.';
        break;
      case 'Submitted':
        description = this.authStore.isAdmin()
          ? `This submission has a pending quote request, last updated ${lastModifiedDate}.`
          : 'Your quote request has been submitted for approval. You will be notified when an underwriter has approved your submission.';
        break;
      case 'Quoted':
        description =
          'Your quote request have been approved by underwriting. Review your submission document now. Ready to Bind? Submit a Bind Request now.';
        break;
      case 'Carrier Review':
        if(this.summary().requestType == "Rewrite"){
        description = 'This submission is an automated rewrite request for an existing policy.';
        }
        else{
        description = 'This submission is an automated renewal request for an existing policy.';
        }
        break;
    case 'More Info':
      description =`${this.summary().underwriterComments}`;
      break;
      case 'Admin Review':
          if(this.summary().requestType == "Rewrite"){
        description = 'This submission is an automated rewrite request for an existing policy.';
        }
        else{
        description = 'This submission is an automated renewal request for an existing policy.';
        }
        break;
      case 'Bind Request':
        description = this.authStore.isAdmin()
          ? `This submission has a pending bind request, last updated ${lastModifiedDate}.`
          : 'Your submission is being reviewed by the underwriting team.';
        break;
      case 'Submission Declined':
      case 'Bind Request Declined':
      case 'Underwriter Review Quote':
      case 'Underwriter Review Bind':
      case 'Producer Review Quote':
      case 'Producer Review Bind':
        description =`${this.summary().underwriterComments}`;
        break;
      case 'Archived':
        break;
      case 'Bound':
        break;
      case 'Issued':
        description = `View the documents issued for policy #${this.summary().policyNumber}.`;
        break;
      case 'Pending Cancellation':
        description = `A notice of cancellation was issued on ${lastModifiedDate}. Resolve the issues to avoid cancellation.`;
        break;
      case 'Cancelled':
        // todo: add reason for cancellation
        description = `Policy was cancelled on ${lastModifiedDate}. Resolve the issues and request a reinstatement to reactivate this policy.`;
        break;
      case 'Flatted':
        description = `A flat cancellation was issued for this policy on ${lastModifiedDate}. No futher action can be taken. `;
        break;
    }
    return description;
  });

  bannerActionButtonTitle = computed(() => {
    var title = ' '; // Default value is space so button doesn't throw missing text error.
    switch (this.summary().status) {
      case 'Indication':
        title = 'Manage Submission';
        break;
      case 'Submitted':
        title = this.authStore.isAdmin() ? 'View Request' : 'Manage Submission';
        break;
      case 'Quoted':
      case 'Producer Review':
        title = this.authStore.isAdmin() ? 'View Request' : 'Download Quote';
        break;
      case 'Carrier Review':
  
        title = this.authStore.isAdmin()
          ? 'View Request'
          : this.authStore.isWholesale()
          ? 'NA'
          : 'NA';
        break;
     case 'More Info':
        title = this.authStore.isAdmin()
          ? 'View Request'
          : this.authStore.isWholesale()
          ? 'Send to Producer'
          : 'Manage Submission';
        break;
      case 'Admin Review':
        title = 'View Request';
        break;
      case 'Bind Request':
        title = this.authStore.isAdmin()
          ? 'View Request'
          : this.authStore.isWholesale()
          ? 'Download Submission'
          : 'Download Quote'
        break;
      case 'Submission Declined':
      case 'Bind Request Declined':
        title ='Review';
        break;
      case 'Underwriter Review Quote':
      case 'Underwriter Review Bind':
        title = 'Send To Producer';
        break;
      case 'Producer Review Quote':
      case 'Producer Review Bind':
        title = 'Edit Submission';
        break;
      case 'Archived':
        break;
      case 'Bound':
        break;
      case 'Issued':
        title = 'View Documents';
        break;
      case 'Pending Cancellation':
        title = 'Download';
        break;
      case 'Cancelled':
        title = 'Request Reinstatement';
        break;
      case 'Flatted':
        title = 'Download';
        break;
    }
    return title;
  });

  bannerActionButtonClicked() {
    if (this.authStore.isAdmin()) {
      switch (this.summary().status) {
        case 'Quoted':
          this.reviewSubmission();
          break;
        case 'Indication':
        case 'Producer Review Quote':
        case 'Producer Review Bind':
          this.quoteAction();
          break;
        case 'Submitted':
        case 'Producer Review':
        case 'Carrier Review':
        case 'Bind Request':
        case 'Bind Request Declined':
          this.adminOnlyRequestModel();
          break;
        case 'Submission Declined':
          this.reviewSubmission();
          break;
        case 'Underwriter Review Quote':
        case 'Underwriter Review Bind':
          this.setStatusAsProducerReview();
          break;
        case 'Issued':
        case 'Pending Cancellation':
        case 'Flatted':
          this.GoToTab('policydocuments');
          break;
        case 'Cancelled':
          this.openAddEndorsementModal('Reinstatement');
          break;
      }
    } else if (this.authStore.isWholesale()) {
      switch (this.summary().status) {
        case 'Indication':
        case 'Producer Review Quote':
        case 'Producer Review Bind':
          this.quoteAction();
          break;
        case 'Submitted':
        case 'Bind Request':
        case 'Submission Declined':
        case 'Producer Review':
        case 'Bind Request Declined':
          this.reviewSubmission();
          break;
        case 'Carrier Review':
          //NA
          break;
        case 'Quoted':
          this.downloadQuote();
          break;
        case 'Underwriter Review Quote':
        case 'Underwriter Review Bind':
          this.setStatusAsProducerReview();
          break;
        case 'Issued':
        case 'Pending Cancellation':
        case 'Flatted':
          this.GoToTab('policydocuments');
          break;
        case 'Cancelled':
          this.openAddEndorsementModal('Reinstatement');
          break;
      }
    } else {
      switch (this.summary().status) {
        case 'Indication':
          this.quoteAction();
          break;
        case 'Submitted':
        case 'More Info':
        case 'Submission Declined':
          this.reviewSubmission();
          break;
        case 'Quoted':
        case 'Producer Review':
        case 'Bind Request Declined':
        case 'Bind Request':
          this.downloadQuote();
          break;
        case 'Producer Review Quote':
        case 'Producer Review Bind':
          this.quoteAction();
          break;
        case 'Issued':
        case 'Pending Cancellation':
       case 'Flatted':
          this.GoToTab('policydocuments');
          break;
        case 'Cancelled':
          this.openAddEndorsementModal('Reinstatement');
          break;
      }
    }
  }
  //#endregion
}
